import { useState, useCallback, useMemo } from 'react';

export type GameMode = 'soft' | 'hard' | 'roulette' | 'coquin';
export type ChoiceType = 'question' | 'action';

interface GameState {
  currentPlayerIndex: number;
  currentChoice: ChoiceType | null;
  currentContent: string | null;
  isCardFlipped: boolean;
  turnCount: number;
}

export function useGameLogic(players: string[], questions: string[], actions: string[]) {
  const [gameState, setGameState] = useState<GameState>({
    currentPlayerIndex: 0,
    currentChoice: null,
    currentContent: null,
    isCardFlipped: false,
    turnCount: 0,
  });

  const shuffledOrder = useMemo(() => {
    const indices = players.map((_, i) => i);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    return indices;
  }, [players]);

  const currentPlayer = useMemo(() => {
    const idx = shuffledOrder[gameState.currentPlayerIndex % shuffledOrder.length];
    return players[idx] || '';
  }, [players, shuffledOrder, gameState.currentPlayerIndex]);

  const getRandomItem = useCallback((items: string[]): string => {
    if (items.length === 0) return "Contenu à venir... 🎉";
    return items[Math.floor(Math.random() * items.length)];
  }, []);

  const flipCard = useCallback(() => {
    setGameState(prev => ({ ...prev, isCardFlipped: true }));
  }, []);

  const chooseType = useCallback((type: ChoiceType) => {
    const content = type === 'question'
      ? getRandomItem(questions)
      : getRandomItem(actions);
    setGameState(prev => ({
      ...prev,
      currentChoice: type,
      currentContent: content,
    }));
  }, [questions, actions, getRandomItem]);

  const getRandomContent = useCallback((): { type: ChoiceType; content: string } => {
    const type: ChoiceType = Math.random() > 0.5 ? 'question' : 'action';
    const content = type === 'question'
      ? getRandomItem(questions)
      : getRandomItem(actions);
    return { type, content };
  }, [questions, actions, getRandomItem]);

  const nextTurn = useCallback(() => {
    setGameState(prev => ({
      currentPlayerIndex: (prev.currentPlayerIndex + 1) % players.length,
      currentChoice: null,
      currentContent: null,
      isCardFlipped: false,
      turnCount: prev.turnCount + 1,
    }));
  }, [players.length]);

  return {
    currentPlayer,
    currentChoice: gameState.currentChoice,
    currentContent: gameState.currentContent,
    isCardFlipped: gameState.isCardFlipped,
    turnCount: gameState.turnCount,
    flipCard,
    chooseType,
    nextTurn,
    getRandomContent,
  };
}
